import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { normalizeBiV2ShotMatrix } from '../../lib/basketball/biV2ShotMatrix';
import { fetchBiV2ProductionData } from '../../lib/basketball/biV2ProductionData';

// NBA half-court dimensions in feet
const COURT_WIDTH = 50;
const COURT_LENGTH = 47;
const HOOP_X = 0;
const HOOP_Z = 5.25;

const DEFAULT_PLAYER = {
  name: 'SPURS BIv2 MATRIX',
  number: '--',
  team: 'SAN ANTONIO SPURS',
  game: '2025-26 CS3S + FREE THROWS',
};

function calcPPS(shots) {
  if (!shots.length) return 0;
  const pts = shots
    .filter((shot) => shot.made)
    .reduce((sum, shot) => sum + (shot.shotClass === '3PT' ? 3 : 2), 0);
  return pts / shots.length;
}

function calcEFG(shots) {
  if (!shots.length) return 0;
  const fgm = shots.filter((shot) => shot.made).length;
  const threeMakes = shots.filter((shot) => shot.made && shot.shotClass === '3PT').length;
  return (fgm + 0.5 * threeMakes) / shots.length;
}

function buildCourt(scene) {
  const floorGeo = new THREE.PlaneGeometry(100, 80);
  const floorMat = new THREE.MeshStandardMaterial({ color: 0xdfc793, roughness: 0.72 });
  const floor = new THREE.Mesh(floorGeo, floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -0.02;
  floor.receiveShadow = true;
  scene.add(floor);

  const courtGeo = new THREE.PlaneGeometry(COURT_WIDTH, COURT_LENGTH);
  const courtMat = new THREE.MeshStandardMaterial({ color: 0xd5bb84, roughness: 0.62 });
  const court = new THREE.Mesh(courtGeo, courtMat);
  court.rotation.x = -Math.PI / 2;
  court.position.set(0, 0, COURT_LENGTH / 2);
  court.receiveShadow = true;
  scene.add(court);

  const paintGeo = new THREE.PlaneGeometry(16, 19);
  const paintMat = new THREE.MeshStandardMaterial({ color: 0xcaa664, roughness: 0.6 });
  const paint = new THREE.Mesh(paintGeo, paintMat);
  paint.rotation.x = -Math.PI / 2;
  paint.position.set(0, 0.005, 9.5);
  scene.add(paint);

  const lineMat = new THREE.LineBasicMaterial({ color: 0xf8f8f0, linewidth: 1 });
  const thinLineMat = new THREE.LineBasicMaterial({ color: 0xf8f8f0, transparent: true, opacity: 0.7 });

  const addLine = (points, material) => {
    const geometry = new THREE.BufferGeometry().setFromPoints(
      points.map((p) => new THREE.Vector3(p[0], 0.01, p[1]))
    );
    scene.add(new THREE.Line(geometry, material || lineMat));
  };

  addLine([[-25, 0], [25, 0]]);
  addLine([[-25, 0], [-25, 47]]);
  addLine([[25, 0], [25, 47]]);
  addLine([[-25, 47], [25, 47]]);
  addLine([[-8, 0], [-8, 19]]);
  addLine([[8, 0], [8, 19]]);
  addLine([[-8, 19], [8, 19]]);

  const freeThrowCircle = [];
  for (let i = 0; i <= 64; i += 1) {
    const angle = (i / 64) * Math.PI * 2;
    freeThrowCircle.push([Math.cos(angle) * 6, 19 + Math.sin(angle) * 6]);
  }
  addLine(freeThrowCircle, thinLineMat);

  const threePointPath = [[-22, 0], [-22, 14]];
  for (let i = 0; i <= 50; i += 1) {
    const angle = Math.PI - (i / 50) * Math.PI;
    const x = Math.cos(angle) * 23.75;
    const z = HOOP_Z + Math.sin(angle) * 23.75;
    if (Math.abs(x) <= 22) threePointPath.push([x, z]);
  }
  threePointPath.push([22, 14], [22, 0]);
  addLine(threePointPath);

  const restrictedArea = [];
  for (let i = 0; i <= 32; i += 1) {
    const angle = Math.PI - (i / 32) * Math.PI;
    restrictedArea.push([Math.cos(angle) * 4, HOOP_Z + Math.sin(angle) * 4]);
  }
  addLine(restrictedArea, thinLineMat);

  const backboard = new THREE.Mesh(
    new THREE.BoxGeometry(6, 3.5, 0.1),
    new THREE.MeshStandardMaterial({ color: 0xdddddd, transparent: true, opacity: 0.35, roughness: 0.3 })
  );
  backboard.position.set(0, 4, 4);
  scene.add(backboard);

  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(0.75, 0.05, 8, 24),
    new THREE.MeshStandardMaterial({ color: 0xcc4400, roughness: 0.4, metalness: 0.5 })
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.set(0, 3.05, HOOP_Z);
  scene.add(rim);

  const net = new THREE.Mesh(
    new THREE.CylinderGeometry(0.75, 0.4, 1.4, 10, 1, true),
    new THREE.MeshStandardMaterial({ color: 0xffffff, wireframe: true, transparent: true, opacity: 0.35 })
  );
  net.position.set(0, 2.3, HOOP_Z);
  scene.add(net);
}

function createMarkerX(x, z) {
  const marker = new THREE.Group();
  const markerMat = new THREE.LineBasicMaterial({ color: 0xcc2222, linewidth: 2 });
  const size = 0.7;

  marker.add(
    new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-size, 0, -size),
        new THREE.Vector3(size, 0, size),
      ]),
      markerMat
    )
  );

  marker.add(
    new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(size, 0, -size),
        new THREE.Vector3(-size, 0, size),
      ]),
      markerMat
    )
  );

  marker.position.set(x, 0.02, z);
  return marker;
}

function createMarkerO(x, z) {
  const circle = new THREE.EllipseCurve(0, 0, 0.6, 0.6, 0, Math.PI * 2, false, 0);
  const geometry = new THREE.BufferGeometry().setFromPoints(
    circle.getPoints(32).map((point) => new THREE.Vector3(point.x, 0, point.y))
  );
  const line = new THREE.Line(
    geometry,
    new THREE.LineBasicMaterial({ color: 0x22aa44, linewidth: 2 })
  );
  line.position.set(x, 0.02, z);
  return line;
}

function createArcPath(startX, startZ, made) {
  const distance = Math.sqrt((startX - HOOP_X) ** 2 + (startZ - HOOP_Z) ** 2);
  const peak = Math.max(12, 10 + distance * 0.15);
  const points = [];

  for (let i = 0; i <= 40; i += 1) {
    const t = i / 40;
    let x = startX + (HOOP_X - startX) * t;
    let z = startZ + (HOOP_Z - startZ) * t;
    let y = 4 * peak * t * (1 - t);

    if (!made && t > 0.85) {
      const deflect = (t - 0.85) / 0.15;
      x += (Math.random() - 0.5) * 3 * deflect;
      z -= 2 * deflect;
      y *= 0.6;
    }

    points.push(new THREE.Vector3(x, y, z));
  }

  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = new THREE.LineDashedMaterial({
    color: made ? 0x22aa44 : 0xcc2222,
    dashSize: 0.5,
    gapSize: 0.3,
    transparent: true,
    opacity: 0.45,
  });
  const line = new THREE.Line(geometry, material);
  line.computeLineDistances();
  return line;
}

function createHitTarget(x, z) {
  const hit = new THREE.Mesh(
    new THREE.PlaneGeometry(2.2, 2.2),
    new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide })
  );
  hit.rotation.x = -Math.PI / 2;
  hit.position.set(x, 0.03, z);
  return hit;
}

class OrbitController {
  constructor(camera, element) {
    this.camera = camera;
    this.target = new THREE.Vector3(0, 0, 18);
    this.spherical = new THREE.Spherical(52, Math.PI / 4.2, Math.PI / 7);
    this.isDragging = false;
    this.previous = { x: 0, y: 0 };

    element.addEventListener('pointerdown', (event) => {
      this.isDragging = true;
      this.previous = { x: event.clientX, y: event.clientY };
    });

    window.addEventListener('pointerup', () => {
      this.isDragging = false;
    });

    element.addEventListener('pointermove', (event) => {
      if (!this.isDragging) return;

      this.spherical.theta -= (event.clientX - this.previous.x) * 0.003;
      this.spherical.phi = Math.max(
        0.12,
        Math.min(1.45, this.spherical.phi - (event.clientY - this.previous.y) * 0.003)
      );

      this.previous = { x: event.clientX, y: event.clientY };
    });

    element.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        this.spherical.radius = Math.max(
          18,
          Math.min(85, this.spherical.radius + event.deltaY * 0.04)
        );
      },
      { passive: false }
    );

    element.addEventListener('contextmenu', (event) => event.preventDefault());
  }

  update() {
    this.camera.position.copy(new THREE.Vector3().setFromSpherical(this.spherical).add(this.target));
    this.camera.lookAt(this.target);
  }

  preset(view) {
    const presets = {
      BROADCAST: [52, 0.74, 0.45],
      OVERHEAD: [45, 0.13, 0],
      BASELINE: [48, 0.78, 0],
      SIDELINE: [38, 0.78, -1.57],
    };

    const [radius, phi, theta] = presets[view] || presets.BROADCAST;
    this.spherical.set(radius, phi, theta);
  }
}

function ShotPopup({ shot, onClose, position, onFullAnalysis, onCompare }) {
  if (!shot) return null;

  const pps = shot.made ? (shot.shotClass === '3PT' ? 3 : 2) : 0;
  const color = shot.made ? '#22aa44' : '#cc2222';
  const mono = "'JetBrains Mono','SF Mono',monospace";
  const hasClip = Boolean(shot.videoUrl && shot.videoUrl !== '#');
  const stepsText = Array.isArray(shot.lastFiveSteps)
    ? (shot.lastFiveSteps.length ? shot.lastFiveSteps.join(' > ') : 'N/A')
    : (shot.lastFiveSteps || 'N/A');
  const footworkText = shot.footworkStyle ? `(${shot.footworkStyle})` : '(unknown)';

  return (
    <div style={{ position: 'absolute', left: position.x, top: position.y, transform: 'translate(-50%,-110%)', zIndex: 100, pointerEvents: 'auto' }}>
      <div style={{ background: '#111', border: '1px solid #282828', borderRadius: 8, width: 360, overflow: 'hidden', fontFamily: mono }}>
        <div style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #1e1e1e' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color, fontSize: 14, fontWeight: 700 }}>{shot.made ? '○' : '✕'}</span>
            <span style={{ color: '#777', fontSize: 10, letterSpacing: 1.5 }}>{shot.type} — {shot.shotClass}</span>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: '#444', fontSize: 14, cursor: 'pointer', padding: 0 }}>✕</button>
        </div>

        <div style={{ height: 160, background: '#0a0a0a', borderBottom: '1px solid #181818' }}>
          {hasClip ? (
            <video
              src={shot.videoUrl}
              controls
              preload="metadata"
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
            />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#444', fontSize: 10, letterSpacing: 1 }}>
              NO CLIP PREVIEW AVAILABLE
            </div>
          )}
        </div>

        <div style={{ padding: '10px 14px', borderBottom: '1px solid #181818' }}>
          <p style={{ color: '#999', fontSize: 11, margin: 0, lineHeight: 1.5 }}>{shot.description}</p>
        </div>

        <div style={{ padding: '8px 14px', borderBottom: '1px solid #181818' }}>
          <div style={{ color: '#444', fontSize: 8, letterSpacing: 1.5, marginBottom: 4 }}>MOVEMENT TRACE</div>
          <div style={{ color: '#777', fontSize: 10, lineHeight: 1.5 }}>Entry: {shot.movementOrigin || 'Unknown origin'}</div>
          <div style={{ color: '#777', fontSize: 10, lineHeight: 1.5 }}>Last 5: {stepsText}</div>
          <div style={{ color: '#777', fontSize: 10, lineHeight: 1.5 }}>Footwork {footworkText}</div>
          <div style={{ color: '#777', fontSize: 10, lineHeight: 1.5 }}>Pass Origin: {shot.passOrigin || 'Unknown'}</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: '1px solid #181818' }}>
          {[
            { label: 'PPS', value: pps.toFixed(1), color: pps >= 1 ? '#22aa44' : '#cc2222' },
            { label: 'SHOT DIST', value: `${shot.dist}ft` },
            { label: 'BALL→HOOP', value: `${shot.ballFromHoop}ft` },
          ].map((item, index) => (
            <div key={item.label} style={{ textAlign: 'center', padding: '9px 0', borderRight: index < 2 ? '1px solid #181818' : 'none' }}>
              <div style={{ color: '#444', fontSize: 8, letterSpacing: 1.5, marginBottom: 2 }}>{item.label}</div>
              <div style={{ color: item.color || '#ccc', fontSize: 14, fontWeight: 700 }}>{item.value}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
          {[
            {
              label: 'DEF DIST',
              value: `${shot.defDist}ft`,
              color: shot.defDist >= 6 ? '#22aa44' : shot.defDist >= 4 ? '#777' : '#cc2222',
            },
            { label: 'CONTEST', value: shot.contestLevel },
            { label: 'QTR / TIME', value: `${shot.quarter} ${shot.time}` },
          ].map((item, index) => (
            <div key={item.label} style={{ textAlign: 'center', padding: '9px 0', borderRight: index < 2 ? '1px solid #181818' : 'none' }}>
              <div style={{ color: '#444', fontSize: 8, letterSpacing: 1.5, marginBottom: 2 }}>{item.label}</div>
              <div style={{ color: item.color || '#ccc', fontSize: 11, fontWeight: 600 }}>{item.value}</div>
            </div>
          ))}
        </div>

        <div style={{ borderTop: '1px solid #181818', padding: '8px 14px', display: 'flex', gap: 6 }}>
          <button
            type="button"
            onClick={() => onFullAnalysis?.(shot)}
            style={{ flex: 1, padding: '6px 0', borderRadius: 4, fontSize: 9, background: 'transparent', border: '1px solid #282828', color: '#aaa', fontWeight: 600, cursor: 'pointer', letterSpacing: 1, fontFamily: mono }}
          >
            FULL ANALYSIS
          </button>
          <button
            type="button"
            onClick={() => onCompare?.(shot)}
            style={{ flex: 1, padding: '6px 0', borderRadius: 4, fontSize: 9, background: 'transparent', border: '1px solid #282828', color: '#aaa', fontWeight: 600, cursor: 'pointer', letterSpacing: 1, fontFamily: mono }}
          >
            COMPARE
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BasketballIntelligenceV2() {
  const location = useLocation();
  const navigate = useNavigate();

  const inputData =
    location.state?.shotMatrixData ||
    location.state?.analysisData ||
    location.state ||
    null;

  const routeNormalized = useMemo(
    () => normalizeBiV2ShotMatrix(inputData, { fallbackShots: [], fallbackPlayer: DEFAULT_PLAYER }),
    [inputData]
  );

  const [normalized, setNormalized] = useState(routeNormalized);
  const [loadingData, setLoadingData] = useState(routeNormalized.shots.length === 0);
  const [dataError, setDataError] = useState(null);
  const [dataMeta, setDataMeta] = useState(inputData?.meta || null);
  const [dataSource, setDataSource] = useState(routeNormalized.shots.length > 0 ? 'analysis-input' : 'production');

  const shots = normalized.shots;
  const player = normalized.player;

  useEffect(() => {
    let cancelled = false;

    if (routeNormalized.shots.length > 0) {
      setNormalized(routeNormalized);
      setLoadingData(false);
      setDataError(null);
      setDataMeta(inputData?.meta || null);
      setDataSource('analysis-input');
      return undefined;
    }

    setLoadingData(true);
    setDataError(null);
    setDataSource('production');

    fetchBiV2ProductionData()
      .then((productionPayload) => {
        if (cancelled) return;
        const normalizedPayload = normalizeBiV2ShotMatrix(productionPayload, {
          fallbackShots: [],
          fallbackPlayer: DEFAULT_PLAYER,
        });
        setNormalized(normalizedPayload);
        setDataMeta(productionPayload?.meta || null);
      })
      .catch((error) => {
        if (cancelled) return;
        setNormalized(normalizeBiV2ShotMatrix(null, { fallbackShots: [], fallbackPlayer: DEFAULT_PLAYER }));
        setDataMeta(null);
        setDataError(error?.message || 'Failed to load production shot data.');
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false);
      });

    return () => {
      cancelled = true;
    };
  }, [routeNormalized, inputData]);

  const mountRef = useRef(null);
  const hitsRef = useRef([]);
  const shotObjectsRef = useRef([]);
  const orbitRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const frameRef = useRef(null);

  const [selected, setSelected] = useState(null);
  const [compareAnchor, setCompareAnchor] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [view, setView] = useState('BROADCAST');

  const mono = "'JetBrains Mono','SF Mono','Fira Code',monospace";
  const sans = "'Inter',-apple-system,sans-serif";

  const matchesFilter = useCallback(
    (shot) => {
      if (filter === 'ALL') return true;
      if (filter === 'MADE') return shot.made;
      if (filter === 'MISSED') return !shot.made;
      return shot.shotClass === filter;
    },
    [filter]
  );

  const filteredShots = useMemo(() => shots.filter((shot) => matchesFilter(shot)), [shots, matchesFilter]);

  const ppsAll = useMemo(() => calcPPS(shots), [shots]);
  const efgAll = useMemo(() => calcEFG(shots), [shots]);
  const fgm = useMemo(() => shots.filter((shot) => shot.made).length, [shots]);
  const pts = useMemo(
    () => shots.filter((shot) => shot.made).reduce((sum, shot) => sum + (shot.shotClass === '3PT' ? 3 : 2), 0),
    [shots]
  );

  const zonePPS = useMemo(() => {
    const zones = ['At Rim', 'Paint', 'Mid-Range', '3PT'];
    return zones.map((zone) => {
      const zoneShots = shots.filter((shot) => shot.shotClass === zone);
      return {
        zone,
        pps: calcPPS(zoneShots),
        attempts: zoneShots.length,
        made: zoneShots.filter((shot) => shot.made).length,
      };
    });
  }, [shots]);

  useEffect(() => {
    setSelected(null);
    setHovered(null);
  }, [shots]);

  useEffect(() => {
    if (!compareAnchor) return;
    if (!shots.some((shot) => shot.id === compareAnchor.id)) {
      setCompareAnchor(null);
    }
  }, [shots, compareAnchor]);

  useEffect(() => {
    const mountElement = mountRef.current;
    if (!mountElement) return undefined;

    const width = mountElement.clientWidth;
    const height = mountElement.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1916);

    const camera = new THREE.PerspectiveCamera(38, width / height, 0.1, 200);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.18;
    rendererRef.current = renderer;
    mountElement.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0x7f6d54, 0.95));

    const keyLight = new THREE.SpotLight(0xfff1d6, 1.9, 100, Math.PI / 4, 0.4, 1);
    keyLight.position.set(0, 38, 22);
    keyLight.castShadow = true;
    scene.add(keyLight);

    const fillLight = new THREE.SpotLight(0xe6d0b3, 0.65, 80, Math.PI / 3, 0.5, 1);
    fillLight.position.set(-25, 22, 8);
    scene.add(fillLight);

    buildCourt(scene);

    const hitTargets = [];
    const shotObjects = [];

    shots.forEach((shot) => {
      const marker = shot.made ? createMarkerO(shot.x, shot.z) : createMarkerX(shot.x, shot.z);
      scene.add(marker);

      const hit = createHitTarget(shot.x, shot.z);
      hit.userData = { shot };
      scene.add(hit);
      hitTargets.push(hit);

      const arc = createArcPath(shot.x, shot.z, shot.made);
      arc.userData = { shotId: shot.id };
      scene.add(arc);

      shotObjects.push({ shot, marker, hit, arc });
    });

    hitsRef.current = hitTargets;
    shotObjectsRef.current = shotObjects;

    const orbit = new OrbitController(camera, renderer.domElement);
    orbit.preset('BROADCAST');
    orbitRef.current = orbit;

    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      orbit.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!mountElement.isConnected) return;
      const nextWidth = mountElement.clientWidth;
      const nextHeight = mountElement.clientHeight;
      camera.aspect = nextWidth / nextHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(nextWidth, nextHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      if (renderer.domElement.parentNode === mountElement) {
        mountElement.removeChild(renderer.domElement);
      }
    };
  }, [shots]);

  useEffect(() => {
    orbitRef.current?.preset(view);
  }, [view]);

  useEffect(() => {
    shotObjectsRef.current.forEach(({ shot, marker, hit, arc }) => {
      const visible = matchesFilter(shot);
      marker.visible = visible;
      hit.visible = visible;
      arc.visible = visible && hovered?.id === shot.id;
    });

    if (selected && !matchesFilter(selected)) {
      setSelected(null);
    }
    if (hovered && !matchesFilter(hovered)) {
      setHovered(null);
    }
  }, [matchesFilter, selected, hovered]);

  const handleFullAnalysis = useCallback((shot) => {
    if (!shot) return;
    navigate('/basketball/ask-slam-ai', {
      state: {
        source: 'bi-v2',
        mode: 'full-analysis',
        player,
        shot,
        context: {
          filter,
          dataSource,
          totalShots: shots.length,
        },
      },
    });
  }, [navigate, player, filter, dataSource, shots.length]);

  const handleCompareShot = useCallback((shot) => {
    if (!shot) return;
    if (!compareAnchor || compareAnchor.id === shot.id) {
      setCompareAnchor(compareAnchor?.id === shot.id ? null : shot);
      return;
    }

    navigate('/basketball/ask-slam-ai', {
      state: {
        source: 'bi-v2',
        mode: 'compare-shots',
        player,
        primaryShot: compareAnchor,
        secondaryShot: shot,
        context: {
          filter,
          dataSource,
          totalShots: shots.length,
        },
      },
    });
    setCompareAnchor(null);
  }, [navigate, player, compareAnchor, filter, dataSource, shots.length]);

  const realtimeRecommendation = useMemo(() => {
    if (!shots.length) return 'Waiting on production shots to seed in-game recommendations.';
    const activeShot = selected || hovered || compareAnchor;
    if (!activeShot) return 'Hover or select a shot to generate a realtime set recommendation.';

    const zone = zonePPS.find((entry) => entry.zone === activeShot.shotClass);
    const zonePpsValue = zone?.pps ?? 0;
    if (zonePpsValue >= 1.0) {
      return `Keep flowing into ${activeShot.shotClass} actions. Re-trigger with weak-side lift and attack early clock before help is set.`;
    }
    return `Current ${activeShot.shotClass} PPS is ${zonePpsValue.toFixed(2)}. Recommend paint-touch trigger then kick to relocation shooter (Gemini/Vertex live scan target).`;
  }, [shots, selected, hovered, compareAnchor, zonePPS]);

  const pickShot = useCallback((event) => {
    if (!rendererRef.current || !cameraRef.current || !mountRef.current) return null;

    const rect = mountRef.current.getBoundingClientRect();
    mouseRef.current.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
    const hits = raycasterRef.current.intersectObjects(hitsRef.current);

    return hits.length > 0 ? hits[0].object.userData.shot : null;
  }, []);

  const handleClick = useCallback((event) => {
    const shot = pickShot(event);
    if (shot) {
      const rect = mountRef.current.getBoundingClientRect();
      setSelected(shot);
      setPopupPosition({ x: event.clientX - rect.left, y: event.clientY - rect.top });
    } else {
      setSelected(null);
    }
  }, [pickShot]);

  const handleMove = useCallback((event) => {
    const shot = pickShot(event);
    setHovered(shot || null);
    if (mountRef.current) {
      mountRef.current.style.cursor = shot ? 'pointer' : 'grab';
    }
  }, [pickShot]);

  return (
    <div style={{ width: '100%', height: '100vh', background: '#0e0e0e', color: '#ccc', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: sans }}>
      <div style={{ height: 46, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', borderBottom: '1px solid #1a1a1a', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 700, letterSpacing: 2, color: '#ddd' }}>BIv2 SHOT MATRIX</span>
          <span style={{ color: '#282828' }}>|</span>
          <span style={{ color: '#444', fontSize: 9, letterSpacing: 1.5, fontFamily: mono }}>BASKETBALL INTELLIGENCE 2.0</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: loadingData ? '#886622' : dataError ? '#cc2222' : '#4a4a4a', fontSize: 8, letterSpacing: 1.5, fontFamily: mono }}>
            {loadingData ? 'LOADING PRODUCTION' : dataError ? 'PRODUCTION LOAD ERROR' : `ATTEMPTS ${shots.length}`}
          </span>
          <span style={{ color: '#2f2f2f', fontSize: 8, letterSpacing: 1.5, fontFamily: mono }}>
            {dataSource === 'analysis-input' ? 'SOURCE INPUT' : 'SOURCE SPURS CS3S+FT'}
          </span>
          {dataMeta?.totalShots ? (
            <span style={{ color: '#3a3a3a', fontSize: 8, letterSpacing: 1.5, fontFamily: mono }}>
              TOTAL {dataMeta.totalShots}
            </span>
          ) : null}
          <div style={{ width: 1, height: 14, background: '#1e1e1e' }} />
          {['BROADCAST', 'OVERHEAD', 'BASELINE', 'SIDELINE'].map((cameraView) => (
            <button
              key={cameraView}
              type="button"
              onClick={() => {
                setView(cameraView);
                orbitRef.current?.preset(cameraView);
              }}
              style={{
                padding: '4px 10px',
                borderRadius: 3,
                border: 'none',
                background: view === cameraView ? '#1e1e1e' : 'transparent',
                color: view === cameraView ? '#ccc' : '#444',
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: 1.5,
                cursor: 'pointer',
                fontFamily: mono,
              }}
            >
              {cameraView}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', position: 'relative' }}>
        <div
          ref={mountRef}
          onClick={handleClick}
          onMouseMove={handleMove}
          onMouseLeave={() => setHovered(null)}
          style={{ flex: 1, cursor: 'grab', position: 'relative' }}
        >
          {loadingData && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 45, background: 'rgba(10,10,10,0.92)', border: '1px solid #222', borderRadius: 6, padding: '12px 16px', fontFamily: mono, fontSize: 10, color: '#888', letterSpacing: 1.2 }}>
              Loading Spurs CS3S + FT production data...
            </div>
          )}
          {!loadingData && dataError && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 45, background: 'rgba(20,10,10,0.92)', border: '1px solid #442222', borderRadius: 6, padding: '12px 16px', fontFamily: mono, fontSize: 10, color: '#cc6666', letterSpacing: 1 }}>
              Production load failed: {dataError}
            </div>
          )}
          {!loadingData && !dataError && shots.length === 0 && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 45, background: 'rgba(10,10,10,0.92)', border: '1px solid #222', borderRadius: 6, padding: '12px 16px', fontFamily: mono, fontSize: 10, color: '#777', letterSpacing: 1 }}>
              No production shot data found for Spurs CS3S and Free Throws.
            </div>
          )}
          <ShotPopup
            shot={selected}
            position={popupPosition}
            onClose={() => setSelected(null)}
            onFullAnalysis={handleFullAnalysis}
            onCompare={handleCompareShot}
          />

          {hovered && !selected && (
            <div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 50, pointerEvents: 'none', background: '#111', border: '1px solid #222', borderRadius: 5, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 12, fontFamily: mono, fontSize: 10 }}>
              <span style={{ color: hovered.made ? '#22aa44' : '#cc2222', fontWeight: 700 }}>{hovered.made ? '○' : '✕'}</span>
              <span style={{ color: '#888' }}>{hovered.type}</span>
              <span style={{ color: '#555' }}>{hovered.dist}ft</span>
              <span style={{ color: '#555' }}>Def:{hovered.defDist}ft</span>
              <span style={{ color: '#555' }}>Hoop:{hovered.ballFromHoop}ft</span>
              <span style={{ color: '#444' }}>click to analyze</span>
            </div>
          )}
        </div>

        <div style={{ width: 270, flexShrink: 0, borderLeft: '1px solid #1a1a1a', background: '#111', display: 'flex', flexDirection: 'column', fontFamily: mono, overflowY: 'auto' }}>
          <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #1a1a1a' }}>
            <div style={{ fontSize: 8, color: '#444', letterSpacing: 2 }}>{player.team}</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#ddd', letterSpacing: 1, marginTop: 2 }}>{player.name}</div>
            <div style={{ fontSize: 9, color: '#383838', marginTop: 2 }}>#{player.number} · {player.game}</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, background: '#1a1a1a', borderBottom: '1px solid #1a1a1a' }}>
            {[
              { label: 'PTS', value: pts },
              { label: 'FG', value: `${fgm}/${shots.length}` },
              { label: 'PPS', value: ppsAll.toFixed(2), hi: ppsAll >= 1.0 },
            ].map((stat) => (
              <div key={stat.label} style={{ background: '#111', textAlign: 'center', padding: '9px 0' }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: stat.hi ? '#22aa44' : '#ddd' }}>{stat.value}</div>
                <div style={{ fontSize: 7, color: '#444', letterSpacing: 2, marginTop: 1 }}>{stat.label}</div>
              </div>
            ))}
          </div>

          <div style={{ padding: '8px 14px', borderBottom: '1px solid #1a1a1a' }}>
            <div style={{ fontSize: 8, color: '#444', letterSpacing: 2, marginBottom: 4 }}>COMPARE QUEUE</div>
            <div style={{ fontSize: 9, color: compareAnchor ? '#888' : '#4a4a4a', lineHeight: 1.5 }}>
              {compareAnchor
                ? `Anchor set: ${compareAnchor.type} ${compareAnchor.time}. Choose a second shot and hit COMPARE.`
                : 'Select any shot, click COMPARE, then select a second shot to launch comparison.'}
            </div>
          </div>

          <div style={{ padding: '8px 14px', borderBottom: '1px solid #1a1a1a' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: 8, color: '#444', letterSpacing: 1.5 }}>eFG%</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: efgAll >= 0.5 ? '#22aa44' : '#cc2222' }}>{(efgAll * 100).toFixed(1)}%</span>
            </div>
            <div style={{ height: 2, background: '#1a1a1a', borderRadius: 1 }}>
              <div style={{ height: '100%', borderRadius: 1, width: `${efgAll * 100}%`, background: efgAll >= 0.5 ? '#22aa44' : '#cc2222' }} />
            </div>
          </div>

          <div style={{ padding: '8px 14px', borderBottom: '1px solid #1a1a1a' }}>
            <div style={{ fontSize: 8, color: '#444', letterSpacing: 2, marginBottom: 6 }}>ZONE PPS</div>
            {zonePPS.map((zone) => (
              <div key={zone.zone} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '3px 0' }}>
                <span style={{ fontSize: 9, color: '#666', minWidth: 55 }}>{zone.zone}</span>
                <span style={{ fontSize: 9, color: '#383838' }}>{zone.made}/{zone.attempts}</span>
                <div style={{ width: 40, height: 2, background: '#1a1a1a', borderRadius: 1 }}>
                  <div style={{ height: '100%', borderRadius: 1, width: `${Math.min((zone.pps / 1.5) * 100, 100)}%`, background: zone.pps >= 1.0 ? '#22aa44' : zone.pps > 0 ? '#886622' : '#282828' }} />
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, minWidth: 32, textAlign: 'right', color: zone.pps >= 1.0 ? '#22aa44' : zone.pps > 0 ? '#886622' : '#383838' }}>{zone.pps.toFixed(2)}</span>
              </div>
            ))}
          </div>

          <div style={{ padding: '8px 14px', borderBottom: '1px solid #1a1a1a' }}>
            <div style={{ fontSize: 8, color: '#444', letterSpacing: 2, marginBottom: 6 }}>SHOT HEATMAP</div>
            <div style={{ position: 'relative', height: 118, borderRadius: 6, border: '1px solid #232323', overflow: 'hidden', background: 'radial-gradient(circle at 24% 62%, rgba(255,140,0,0.25) 0%, rgba(0,0,0,0) 38%), radial-gradient(circle at 73% 36%, rgba(255,90,0,0.28) 0%, rgba(0,0,0,0) 30%), linear-gradient(180deg, #181818 0%, #111 100%)' }}>
              <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize: '16px 16px' }} />
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5a5a5a', fontSize: 9, letterSpacing: 1.1 }}>
                DYNAMIC HEATMAP PLACEHOLDER
              </div>
            </div>
          </div>

          <div style={{ padding: '8px 14px', borderBottom: '1px solid #1a1a1a' }}>
            <div style={{ fontSize: 8, color: '#444', letterSpacing: 2, marginBottom: 5 }}>REALTIME SET RECS</div>
            <div style={{ fontSize: 9, color: '#757575', lineHeight: 1.55 }}>
              {realtimeRecommendation}
            </div>
          </div>

          <div style={{ padding: '8px 14px', borderBottom: '1px solid #1a1a1a' }}>
            <div style={{ fontSize: 8, color: '#444', letterSpacing: 2, marginBottom: 5 }}>FILTER</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {['ALL', 'MADE', 'MISSED', 'At Rim', 'Paint', 'Mid-Range', '3PT'].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFilter(value)}
                  style={{
                    padding: '3px 7px',
                    borderRadius: 2,
                    border: 'none',
                    background: filter === value ? '#1e1e1e' : 'transparent',
                    color: filter === value ? '#ccc' : '#444',
                    fontSize: 8,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: mono,
                  }}
                >
                  {value === 'At Rim' ? 'RIM' : value === 'Mid-Range' ? 'MID' : value}
                </button>
              ))}
            </div>
          </div>

          <div style={{ flex: 1, padding: '6px 14px', overflowY: 'auto' }}>
            <div style={{ fontSize: 8, color: '#444', letterSpacing: 2, marginBottom: 5 }}>SHOT LOG ({filteredShots.length})</div>
            {filteredShots.length === 0 ? (
              <div style={{ padding: '8px 0', fontSize: 9, color: '#4a4a4a' }}>
                No shots in current filter.
              </div>
            ) : filteredShots.map((shot) => (
              <div
                key={shot.id}
                onClick={() => {
                  setSelected(shot);
                  setPopupPosition({
                    x: mountRef.current ? mountRef.current.clientWidth / 2 : 300,
                    y: mountRef.current ? mountRef.current.clientHeight / 2 : 250,
                  });
                }}
                style={{
                  padding: '6px 6px',
                  marginBottom: 1,
                  borderRadius: 3,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  background: selected?.id === shot.id ? '#1a1a1a' : compareAnchor?.id === shot.id ? '#171515' : 'transparent',
                }}
              >
                <span style={{ color: shot.made ? '#22aa44' : '#cc2222', fontSize: 11, fontWeight: 700, width: 12 }}>{shot.made ? '○' : '✕'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 9, color: '#999' }}>{shot.type} · {shot.dist}ft</div>
                  <div style={{ fontSize: 8, color: '#383838' }}>{shot.quarter} {shot.time} · Def {shot.defDist}ft · Hoop {shot.ballFromHoop}ft</div>
                  <div style={{ fontSize: 8, color: '#313131' }}>
                    {`${shot.movementOrigin || 'Unknown move'} · Feet (${shot.footworkStyle || 'unknown'}) · Pass ${shot.passOrigin || 'unknown'}`}
                  </div>
                </div>
                <div style={{ fontSize: 9, fontWeight: 700, color: shot.made ? '#22aa44' : '#383838' }}>
                  {shot.made ? (shot.shotClass === '3PT' ? '3pt' : '2pt') : '0pt'}
                </div>
              </div>
            ))}
          </div>

          <div style={{ padding: '8px 14px', borderTop: '1px solid #1a1a1a', display: 'flex', justifyContent: 'center', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ color: '#22aa44', fontWeight: 700, fontSize: 11 }}>○</span>
              <span style={{ color: '#444', fontSize: 8, letterSpacing: 1 }}>MAKE</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ color: '#cc2222', fontWeight: 700, fontSize: 11 }}>✕</span>
              <span style={{ color: '#444', fontSize: 8, letterSpacing: 1 }}>MISS</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
